import {
  focusHostPage,
  OPEN_MODAL_FOCUS_REJECTED,
  OPEN_MODAL_RETURN_REJECTED,
  type WindowControlsBridge,
} from '@hibro/platform-renderer';

import { readHostIdentity } from './host-identity';

/** `openModal` 没能把焦点还给唤起它的那扇窗口。 */
function warnOpenModalReturnRejected(reason: string): void {
  window.hibroLog?.append({
    level: 'warn',
    kind: 'log',
    message: OPEN_MODAL_RETURN_REJECTED,
    data: { reason },
  });
}

/** `openModal` 没能把本窗口叫到前台。弹层已经打开，这条只说明窗口还在后台。 */
function warnOpenModalFocusRejected(reason: string): void {
  window.hibroLog?.append({
    level: 'warn',
    kind: 'log',
    message: OPEN_MODAL_FOCUS_REJECTED,
    data: { reason },
  });
}

/**
 * Build the window-controls bridge from the preload `hibroWindow` global (§5.8).
 * Lives in the app shell so `platform-renderer` stays free of the preload bridge
 * (same pattern as `createIframeBridge`); shared by the full workbench window
 * and the detached-surface window so both get identical title-bar controls.
 *
 * The web runtime has no preload and no window to control, so it gets
 * `chrome: 'none'` — {@link WindowControls} draws nothing and `CustomHeader`
 * reserves no traffic-light spacer. The OS is deliberately *not* read here:
 * whether we draw chrome and which OS the user is on are separate facts, and the
 * latter lives on `window.hibroHost` for module UI to read too (见 18).
 * Desktop browser shell (`?shell=browser`) uses the same preload + bridge as
 * the workbench (见 22).
 */
export function createWindowControlsBridge(): WindowControlsBridge | undefined {
  const identity = readHostIdentity();
  const channel = identity?.channel;
  const api = window.hibroWindow;
  if (api) {
    return {
      // macOS keeps its native traffic lights; every other Electron window is
      // frameless and draws its own.
      chrome: identity?.os === 'darwin' ? 'native' : 'custom',
      ...(channel ? { channel } : {}),
      minimize: (...args) => void api.minimize(...args),
      toggleMaximize: (...args) => void api.toggleMaximize(...args),
      close: (...args) => void api.closeSelf(...args),
      isMaximized: (...args) => api.isMaximized(...args),
      onMaximizeChange: (...args) => api.onMaximizeChange(...args),
      // 窗口失活（失焦）/激活（聚焦）订阅：透传 preload `hibroWindow`。
      onWindowBlur: (...args) => api.onWindowBlur(...args),
      // 真全屏 / 置顶 / 尺寸控制：透传 preload `hibroWindow`（作用于本窗口）。
      toggleFullScreen: (...args) => void api.toggleFullScreen(...args),
      isFullScreen: (...args) => api.isFullScreen(...args),
      setAlwaysOnTop: (...args) => void api.setAlwaysOnTop(...args),
      isAlwaysOnTop: (...args) => api.isAlwaysOnTop(...args),
      setSize: (...args) => void api.setSize(...args),
      setMinimumSize: (...args) => void api.setMinimumSize(...args),
      getSize: (...args) => api.getSize(...args),
      setPosition: (...args) => void api.setPosition(...args),
      present: () =>
        api.showSelf().then(
          (token) => {
            if (token == null) {
              warnOpenModalFocusRejected('sender window is gone');
              return undefined;
            }
            if (token === 0) {
              return undefined;
            }
            return () =>
              api.restoreFocus(token).then((result) => {
                if (result === 'returned' || result === 'held') {
                  return;
                }
                warnOpenModalReturnRejected(result);
              });
          },
          (error: unknown) => {
            warnOpenModalFocusRejected(
              error instanceof Error ? error.message : String(error),
            );
            return undefined;
          },
        ),
    };
  }
  if (!identity) {
    return undefined;
  }
  return {
    chrome: 'none',
    channel: identity.channel,
    minimize: () => undefined,
    toggleMaximize: () => undefined,
    close: () => undefined,
    isMaximized: () => Promise.resolve(false),
    onMaximizeChange: () => () => undefined,
    // 无 preload（web 运行时）：无窗口可控制，失活订阅 no-op。
    onWindowBlur: () => () => undefined,
    // 无 preload（web 运行时）：无窗口可控制；真全屏退化为浏览器原生 Fullscreen API，
    // 全屏整个 tab（页面本身就是游离窗，故语义对齐桌面「铺满显示器」）。requestFullscreen
    // 需要用户手势且可能被拒，fire-and-forget 并吞掉 rejection，避免 unhandled rejection。
    toggleFullScreen: () => {
      if (document.fullscreenElement) {
        void document.exitFullscreen().catch(() => undefined);
      } else {
        void document.documentElement
          .requestFullscreen()
          .catch(() => undefined);
      }
    },
    isFullScreen: () => Promise.resolve(document.fullscreenElement != null),
    setAlwaysOnTop: () => undefined,
    isAlwaysOnTop: () => Promise.resolve(false),
    setSize: () => undefined,
    // 无 preload（web 运行时）：无窗口最小尺寸约束可设，no-op。
    setMinimumSize: () => undefined,
    getSize: () => Promise.resolve({ width: 0, height: 0 }),
    setPosition: () => undefined,
    present: () => {
      if (!focusHostPage()) {
        warnOpenModalFocusRejected('the page stayed in the background');
      }
      return Promise.resolve(undefined);
    },
  };
}
